import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { NoItemContent } from "../page-bases/NoItemContent";
import { Box, Stack } from "@mui/material";

import { TRowItem } from "../../components/Table/TableRow";
import DamsTable from "../../components/Table/CollapsibleTable";
import { distributionDispatchApi } from "../../services/distribution/distribution-dispatch";
import useAppNavigation from "../../hooks/useAppNavigation";

import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { DispatchLogsFilter } from "../../common-ui/page-filters/DispatchLogsFilter";
import { DispatchRecord, IPagination, IQueryPerams } from "../../config/types";
import { formatDate } from "../../utils/common";
import { BadgeLabel } from "../../components/Label";
import { useAppDispatch } from "../../store/hooks";
import { updatedispatchCart } from "../../store/reducers/distribution/dispatchRecord";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import appRoutePath from "../../config/path";

function AssetDispatchLogsPage() {
  const [pageState, setPageState] = useState("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset, false);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(distributionDispatchApi.getAllDispatch);
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const populateDataSet = (
    data: DispatchRecord[],
    pagination?: IPagination
  ) => {
    const formatData = data.map((data) => ({
      ...data,
      dispatchDate: formatDate(data.dispatch_date),
      origin: `${data.from_location} (${data.from_location_domain})`,
      destination: `${data.to_location} (${data.to_location_domain})`,
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    const result = await distributionDispatchApi.getAllDispatch({
      is_deleted: false,
    });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useAppNavigation();
  function addNewItem() {
    navigate({ path: appRoutePath.ADD_ASSET_DISPATCH_PAGE });
  }

  function clearSearch() {
    setPageState("empty-db");
  }
  const dispatch = useAppDispatch();
  const renderTableLinkActions = ({ item }: { item: TRowItem }) => {
    const toggleDetailButton = async () => {
      dispatch(updatedispatchCart(item as DispatchRecord));
      navigate({
        path: appRoutePath.ASSET_DISPATCH_DETAIL_PAGE,
        params: { id: item.id },
      });
    };

    return (
      <Stack direction={"row"} spacing={1} pl={2}>
        <BadgeLabel
          onClick={toggleDetailButton}
          title={"Details"}
          colorType="blue"
          tip="Go details page"
        />
      </Stack>
    );
  };

  const toggleDeleteRecord = async (data: TRowItem) => {
    actionDialogStore.open({
      title: "Delete Schedule",
      subtitle:
        "Once you delete this record, ONLY an admin can restore it. Are you sure you want to proceed?",
      onRightBtnPress: () => handleDeleteRequest(data.id),
      rightBtnTxt: "Confirm",
      onLeftBtnPress: () => {},
      type: "DELETE",
    });
  };

  async function handleDeleteRequest(id: string) {
    const res = await distributionDispatchApi.deleteDispatch(id ?? "");
    if (res.success) {
      await fetchData();
      resetSelectedRowItems();
      alertDialogStore.open({
        title: res.success ? "Dispatch Deleted Successfully" : "Delete Failed",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }
  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };

    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={distributionDispatchApi.deleteDispatch}
          idKey={"id"}
        />
      </Stack>
    );
  };
  return (
    <MainPageBase
      pageState={pageState}
      title={"Asset Dispatch"}
      // subtitle={"manage assets dispatch"}
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText={"Add Asset Dispatch"}
      exportBtnOnClick={() => {
        alert("Exporting...");
      }}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={""}
            subtitle={""}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"}>
            <DispatchLogsFilter<DispatchRecord[]>
              ftn={distributionDispatchApi.getAllDispatch}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />
            <DamsTable
              tableTitle="Assets Dispatch List"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={6}
              onTableItemDelete={toggleDeleteRecord}
              // hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              headerDropDownMenu={renderHeaderDropDownMenu}
              extraRowComponent={renderTableLinkActions}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const tkeys = [
  "courier_service_provider",
  "dispatchDate",
  "distribution_batch_number",
  "origin",
  "destination",
];

const columns = [
  "Courier Service Provider",
  "Dispatch Date",
  "Distribution Batch No",
  "From",
  "To",
];

const hkeys = ["courier_mode_details", "package_details", "remarks"];

const hcolumns = ["Courier Mode Details", "Package Information", "Remarks"];

export default AssetDispatchLogsPage;
